import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useState } from 'react';
import {
  LayoutDashboard, ArrowLeftRight, CreditCard, Receipt, Send, Landmark,
  Building2, TrendingUp, List, ShieldCheck, LogOut, Menu, X, User, Settings, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'sidebar-nav-dashboard' },
  { to: '/accounts', label: 'Accounts', icon: Building2, testId: 'sidebar-nav-accounts' },
  { to: '/transfer', label: 'Transfer', icon: ArrowLeftRight, testId: 'sidebar-nav-transfer' },
  { to: '/wire', label: 'Wire Transfer', icon: Landmark, testId: 'sidebar-nav-wire' },
  { to: '/bill-pay', label: 'Pay Bills', icon: Receipt, testId: 'sidebar-nav-bill-pay' },
  { to: '/zelle', label: 'Zelle', icon: Send, testId: 'sidebar-nav-zelle' },
  { to: '/cards', label: 'Cards', icon: CreditCard, testId: 'sidebar-nav-cards' },
  { to: '/external-accounts', label: 'External Accounts', icon: ExternalLink, testId: 'sidebar-nav-external-accounts' },
  { to: '/social-security', label: 'Social Security', icon: ShieldCheck, testId: 'sidebar-nav-social-security' },
  { to: '/fx-rates', label: 'Exchange Rates', icon: TrendingUp, testId: 'sidebar-nav-fx-rates' },
  { to: '/transactions', label: 'Transactions', icon: List, testId: 'sidebar-nav-transactions' },
  { to: '/settings', label: 'Settings', icon: Settings, testId: 'sidebar-nav-settings' },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Mega Millions</p>
              <p className="text-[11px] opacity-60">Reserve Escrow Bank</p>
            </div>
          </div>
          <button className="md:hidden absolute top-4 right-4 text-white/70" onClick={() => setMobileOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#0B5CAB] flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name || user?.username}</p>
              <p className="text-[11px] opacity-60 truncate">@{user?.username}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-nav-item w-full text-red-300 hover:text-red-200 hover:bg-red-900/20"
            data-testid="logout-button"
          >
            <LogOut className="h-[18px] w-[18px]" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content flex-1">
        {/* Top header */}
        <div className="top-header flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Mega Millions Reserve Escrow Bank</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">Welcome, {user?.full_name || user?.username}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} className="hidden sm:flex" data-testid="profile-menu-button">
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>

        <Outlet />
      </main>
    </div>
  );
}
